import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  Avatar,
} from '@mui/material';
import { MdVisibility, MdVisibilityOff, MdLock, MdPersonOutline } from 'react-icons/md';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import logo from '../logo.svg';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ usernameOrEmail: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.usernameOrEmail.trim() || !form.password) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      login(form.usernameOrEmail.trim(), form.password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0b0c10 0%, #1a1b2e 50%, #16213e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <SEOMeta
        title="Admin Login | Algodoor Training Dashboard"
        description="Secure login for Algodoor instructors and administrators."
        keywords="admin login, instructor portal, training dashboard"
        url="/login"
        type="WebPage"
        noindex
      />
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 4,
          background: 'rgba(255,255,255,0.04)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Logo + Header */}
          <Box sx={{ textAlign: 'center', mb: 3.5 }}>
            <Box
              component="img"
              src={logo}
              alt="Algodoor"
              sx={{ height: 36, mb: 2, filter: 'brightness(0) invert(1)' }}
            />
            <Avatar
              sx={{
                mx: 'auto',
                mb: 1.5,
                width: 56,
                height: 56,
                background: 'linear-gradient(135deg, #AE67FA, #F49867)',
                boxShadow: '0 4px 20px rgba(174,103,250,0.4)',
              }}
            >
              <MdLock style={{ fontSize: 28, color: 'white' }} />
            </Avatar>
            <Typography variant="h5" fontWeight={700} color="white" letterSpacing={0.5}>
              Welcome back
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.5)" mt={0.5}>
              Sign in to your Algodoor account
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                borderRadius: 2,
                background: 'rgba(211,47,47,0.15)',
                border: '1px solid rgba(211,47,47,0.3)',
                color: '#ff8a8a',
                '& .MuiAlert-icon': { color: '#ff8a8a' },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              name="usernameOrEmail"
              label="Username or Email"
              value={form.usernameOrEmail}
              onChange={handleChange}
              autoComplete="username"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdPersonOutline style={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldStyle}
              margin="normal"
            />
            <TextField
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdLock style={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      sx={{ color: 'rgba(255,255,255,0.4)' }}
                    >
                      {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldStyle}
              margin="normal"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2.5,
                py: 1.4,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #AE67FA 0%, #F49867 100%)',
                color: '#120a20',
                fontWeight: 700,
                fontSize: '1rem',
                letterSpacing: 0.5,
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(174,103,250,0.38)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #b579fb 0%, #f6a67b 100%)',
                  boxShadow: '0 6px 25px rgba(174,103,250,0.5)',
                },
                '&:disabled': { opacity: 0.6 },
              }}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </Box>

          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }}>
            <Typography variant="caption" color="rgba(255,255,255,0.35)">
              OR
            </Typography>
          </Divider>

          <Typography variant="body2" align="center" color="rgba(255,255,255,0.5)">
            Don&apos;t have an account?&nbsp;
            <Link
              to="/signup"
              style={{
                color: '#ff6b6b',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Sign Up
            </Link>
          </Typography>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              to="/"
              style={{
                color: 'rgba(255,255,255,0.35)',
                fontSize: '0.8rem',
                textDecoration: 'none',
              }}
            >
              ← Back to Home
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const fieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#AE67FA' },
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 2,
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.45)',
    '&.Mui-focused': { color: '#f8c7ac' },
  },
};

export default LoginPage;
